<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" elevation="0" title="Overall survival Kaplan-Meier estimates">
      <v-card-subtitle>Use the information icon, in the top right corner, to see the population definition, and
        use the comparison groups below to display different segments of the population.</v-card-subtitle>
      <v-row no-gutters>
        <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
          <table-box title="Follow-up length"
                     :headers="followUpTimeHeaders"
                     :data="followUpTimeData"
                     :comment="followUpTimeComment"/>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
          <table-box title="Median survival time"
                     :headers="survivalTimeHeaders"
                     :data="survivalTimeData"
                     :comment="survivalTimeComment"/>
        </v-col>
        <v-col cols="12" sm="10" md="8" lg="6" xl="4" xxl="4">
          <v-row class="mt-10 mr-5">
            <v-col>
              <v-select v-model="selectedStratifyItems" label="Comparison groups"
                        :items="selectStratifyItems" :multiple="true">
                <template #item="{ props }">
                  <v-list-subheader v-if="props.header">{{ props.header }}</v-list-subheader>
                  <v-divider v-else-if="props.divider" class="mt-2" />
                  <v-list-item v-else v-bind="props" class="ml-2"></v-list-item>
                </template>
              </v-select>
            </v-col>
            <v-col>
              <v-btn variant="plain" @click="toggleAllNoneStratifyItems">All/None</v-btn>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col cols="12">
              <v-checkbox v-model="kaplanMeierConfidenceIntervals" label="Show confidence intervals"
                          hide-details></v-checkbox>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-card class="mx-auto" min-height="800" elevation="0">
            <GChart type="LineChart" :data="survivalChartData"
                    :options="survivalChartOptions"
                    :key="'survivalChart' + survivalChartKeyIndex"
                    v-if="survivalChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
          </v-card>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12" lg="4" xl="4" xxl="4">
          <v-card class="mx-auto" elevation="0"
                  title="Age at death"
                  ref="age-at-death">
            <v-card-subtitle class="text-wrap">{{this.ageAtDeathComment}}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="ageAtDeathHeaders"
                              :items="ageAtDeathData"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['age-at-death'].$el"
                              :document-title="icd + '-' + 'age-at-death'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="0" lg="1" xl="1" xxl="1"/>
        <v-col cols="12" lg="6" xl="6" xxl="6">
          <v-card class="mx-auto" elevation="0"
                  title="Survival rate"
                  ref="survival-rate">
            <v-card-subtitle class="text-wrap">{{this.survivalRateComment}}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="survivalRateHeaders"
                              :items="survivalRateData"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular color="primary" indeterminate></v-progress-circular>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['survival-rate'].$el"
                              :document-title="icd + '-' + 'survival-rate'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";
import { VueToPrint } from "vue-to-print";
import TableBox from "@/components/TableBox.vue";

export default defineComponent({
  name: 'InternalOrderCancerOverallSurvival',
  props: {
    icd: String,
    visible: Boolean
  },
  components: {
    GChart,
    VueToPrint,
    TableBox
  },
  data: () => ({
    kaplanMeierConfidenceIntervals: false,
    selectStratifyItems: [],
    selectedStratifyItems: ['All'],
    followUpTimeHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Mean (SD)',
        align: 'start',
        key: 'mean',
      },
      {
        title: 'Median (Q1, Q3)',
        align: 'start',
        key: 'median',
      },
      {
        title: 'Range (Min, Max)',
        align: 'start',
        key: 'range',
      }
    ],
    followUpTimeData: [],
    followUpTimeComment: '',
    survivalTimeHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Median survival time',
        align: 'start',
        key: 'median_survival_time',
      }
    ],
    survivalTimeData: [],
    survivalTimeComment: '',
    survivalChartKeyIndex: 0,
    survivalChartData: [],
    survivalChartOptions: {
      title: '',
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c', '#bdbdc7'],
      titleTextStyle: {
        fontSize: 16,
        color: '#ffffff',
        bold: true
      },
      legend: {
        position: 'bottom',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      hAxis: {
        title: 'Year after index date',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        }
      },
      vAxis: {
        title: 'Probability of survival',
        baselineColor: '#f0f0f0',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        textStyle: {
          color: '#ffffff'
        },
        gridlines: {
          color: '#c0c0c0',
          interval: 0.25
        },
        minorGridlines: {
          count: 0
        }
      },
      intervals: { 'style': 'area' },
      tooltip: {
        isHtml: true
      }
    },
    ageAtDeathHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label',
      },
      {
        title: 'Number of deceased patients',
        align: 'start',
        key: 'number_of_patients',
      },
      {
        title: 'Mean (SD)',
        align: 'start',
        key: 'mean_age',
      },
      {
        title: 'Median (Q1, Q3)',
        align: 'start',
        key: 'median_age',
      },
      {
        title: 'Range (Min, Max)',
        align: 'start',
        key: 'range_age',
      }
    ],
    ageAtDeathData: [],
    ageAtDeathComment: '',
    survivalRateHeaders: [
      {
        title: 'Patient population',
        align: 'start',
        key: 'label'
      },
      {
        title: '1-year survival rate',
        align: 'start',
        key: 'one_year_survival_rate'
      },
      {
        title: '3-year survival rate',
        align: 'start',
        key: 'three_year_survival_rate'
      },
      {
        title: '5-year survival rate',
        align: 'start',
        key: 'five_year_survival_rate'
      },
      {
        title: '10-year survival rate',
        align: 'start',
        key: 'ten_year_survival_rate'
      },
      {
        title: '15-year survival rate',
        align: 'start',
        key: 'fifteen_year_survival_rate'
      }
    ],
    survivalRateData: [],
    survivalRateComment: '',
  }),
  watch: {
    selectedStratifyItems() {
      this.loadDynamicData()
    },
    kaplanMeierConfidenceIntervals() {
      this.loadDynamicData()
    }
  },
  methods: {
    forceRerender() {
      this.survivalChartKeyIndex += 1;
    },
    async loadSurvivalComparisonGroups() {
      let data = axios.get('/cancer-internal-order/survival-comparison-groups',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
      return data.then((response) => {
        this.selectStratifyItems = [...response.data]
      });
    },
    async loadFollowUpTimeData() {
      let data = axios.get('/cancer-internal-order/follow-up-time-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              stratify_items: this.selectedStratifyItems.join(",")
            }
          });
      return data.then((response) => {
        this.followUpTimeData = response.data.data
        this.followUpTimeComment = response.data.comment
      });
    },
    async loadSurvivalTimeData() {
      let data = axios.get('/cancer-internal-order/survival-time-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              stratify_items: this.selectedStratifyItems.join(",")
            }
          });
      return data.then((response) => {
        this.survivalTimeData = response.data.data
        this.survivalTimeComment = response.data.comment
      });
    },
    async loadSurvivalData() {
      let data = axios.get('/cancer-internal-order/survival',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              stratify_items: this.selectedStratifyItems.join(","),
              kaplan_meier_confidence_intervals: this.kaplanMeierConfidenceIntervals
            }
          });
      return data.then((response) => {
        this.survivalChartData = response.data
      });
    },
    async loadAgeAtDeathData() {
      let data = axios.get('cancer-internal-order/age-at-death-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              stratify_items: this.selectedStratifyItems.join(",")
            }
          });
      return data.then((response) => {
        this.ageAtDeathData = response.data.data
        this.ageAtDeathComment = response.data.comment
      });
    },
    async loadSurvivalRateData() {
      let data = axios.get('/cancer-internal-order/survival-rate-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              stratify_items: this.selectedStratifyItems.join(",")
            }
          });
      return data.then((response) => {
        this.survivalRateData = response.data.data
        this.survivalRateComment = response.data.comment
      });
    },
    toggleAllNoneStratifyItems() {
      if (this.selectedStratifyItems.length === this.selectStratifyItems.length) {
        this.selectedStratifyItems = [];
      } else {
        this.selectedStratifyItems = [...this.selectStratifyItems]
      }
    },
    async loadStaticData() {
      return await this.loadSurvivalComparisonGroups().then(() => {
        console.log('Static data loaded: InternalOrderCancerOverallSurvival')
      })
    },
    async loadDynamicData() {
      return await Promise.all([
          this.loadFollowUpTimeData(),
          this.loadSurvivalTimeData(),
          this.loadSurvivalData(),
          this.loadAgeAtDeathData(),
          this.loadSurvivalRateData()
      ]).then(() => {
        console.log('Dynamic data loaded: InternalOrderCancerOverallSurvival')
      })
    }
  },
  mounted() {
    this.loadStaticData().then(() => {
      return this.loadDynamicData()
    })
  },
  updated() {
    if (this.visible) {
      this.forceRerender()
    }
  }
});
</script>

<style scoped>
.striped-table :deep(tbody tr:nth-child(odd)) {
  background-color: rgba(0, 0, 0, .10);
}
</style>
